import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import api from "services/api";

/**
 * Database statistics (2026-10-21).
 *
 * Row counts are InnoDB ESTIMATES from information_schema — accurate enough to
 * spot a table that exploded, without scanning millions of rows on page load.
 * Sizes are exact.
 */

const formatBytes = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return "—";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / 1024 / 1024).toFixed(1)} MB`;
  return `${(value / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const formatCount = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return numeric.toLocaleString();
};

const SummaryCard = ({ label, value, hint }) => {
  const theme = useTheme();

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: "12px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Paper>
  );
};

const ServerDatabase = () => {
  const theme = useTheme();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("totalBytes");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/server/database");
      setData(response.data || null);
      setError("");
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail ||
          requestError?.response?.data?.error ||
          "Failed to load database statistics."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const tables = useMemo(() => {
    const list = Array.isArray(data?.tables) ? [...data.tables] : [];
    const needle = search.trim().toLowerCase();
    const filtered = needle
      ? list.filter((table) => table.table.toLowerCase().includes(needle))
      : list;

    return filtered.sort((left, right) => {
      if (sortKey === "table") return left.table.localeCompare(right.table);
      return (Number(right[sortKey]) || 0) - (Number(left[sortKey]) || 0);
    });
  }, [data, search, sortKey]);

  const maxBytes = useMemo(
    () =>
      tables.reduce(
        (max, table) => (Number(table.totalBytes) > max ? Number(table.totalBytes) : max),
        0
      ),
    [tables]
  );

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const totals = data?.totals || {};
  const pool = data?.pool || null;

  // A mixed collation set throws "Illegal mix of collations" at compare time.
  const mixedCollations = (data?.collations || []).length > 1;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {error && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 6, md: 3 }}>
          <SummaryCard label="Tables" value={formatCount(totals.tables)} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <SummaryCard
            label="Rows (estimated)"
            value={formatCount(totals.rowEstimate)}
            hint="InnoDB estimate"
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <SummaryCard
            label="Data size"
            value={formatBytes(totals.dataBytes)}
            hint={`Indexes ${formatBytes(totals.indexBytes)}`}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <SummaryCard
            label="Total size"
            value={formatBytes(totals.totalBytes)}
            hint={data?.database || ""}
          />
        </Grid>
      </Grid>

      {/* Pool + collation health ------------------------------------------- */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: "12px", height: "100%" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Connection pool
            </Typography>
            {pool ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography variant="body2">
                  {pool.using ?? 0} in use · {pool.available ?? 0} idle ·{" "}
                  {pool.size ?? 0} total
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {pool.waiting > 0
                    ? `${pool.waiting} request(s) waiting for a connection — pool is saturated.`
                    : "No requests waiting."}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Pool statistics unavailable.
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: "12px",
              height: "100%",
              ...(mixedCollations
                ? {
                    borderColor: `${theme.palette.warning.main}66`,
                    bgcolor: `${theme.palette.warning.main}0d`,
                  }
                : {}),
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Collations
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {(data?.collations || []).map((entry) => (
                <Box
                  key={entry.collation}
                  sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
                >
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {entry.collation || "unknown"}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${entry.tableCount} table(s)`}
                    sx={{ height: 20, borderRadius: "6px", fontSize: "0.7rem" }}
                  />
                </Box>
              ))}
              {mixedCollations && (
                <Typography variant="caption" sx={{ color: theme.palette.warning.main, mt: 0.5 }}>
                  More than one collation is in use — comparing these tables in a
                  join can raise “Illegal mix of collations”.
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Table list --------------------------------------------------------- */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          placeholder="Filter tables…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ minWidth: 240, "& .MuiInputBase-input": { py: 0.75 } }}
        />

        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshRoundedIcon />}
          onClick={load}
          disabled={loading}
          sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
        >
          Reload
        </Button>

        <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
          {data?.note || ""}
        </Typography>
      </Box>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: "12px", maxHeight: "60vh" }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {[
                { key: "table", label: "Table" },
                { key: "rowEstimate", label: "Rows (est.)", align: "right" },
                { key: "dataBytes", label: "Data", align: "right" },
                { key: "indexBytes", label: "Index", align: "right" },
                { key: "totalBytes", label: "Total", align: "right" },
              ].map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align || "left"}
                  onClick={() => setSortKey(column.key)}
                  sx={{
                    fontWeight: 700,
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    color:
                      sortKey === column.key
                        ? theme.palette.primary.main
                        : theme.palette.text.primary,
                  }}
                >
                  {column.label}
                  {sortKey === column.key ? " ↓" : ""}
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 700, width: "18%" }}>Share</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tables.map((table) => {
              const share = maxBytes > 0 ? (table.totalBytes / maxBytes) * 100 : 0;

              return (
                <TableRow key={table.table} hover>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                    {table.table}
                  </TableCell>
                  <TableCell align="right">{formatCount(table.rowEstimate)}</TableCell>
                  <TableCell align="right">{formatBytes(table.dataBytes)}</TableCell>
                  <TableCell align="right">{formatBytes(table.indexBytes)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {formatBytes(table.totalBytes)}
                  </TableCell>
                  <TableCell>
                    <LinearProgress
                      variant="determinate"
                      value={share}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: theme.palette.action.hover,
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 3,
                          bgcolor: theme.palette.primary.main,
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}

            {tables.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">
                    No tables match “{search}”.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ServerDatabase;
